import { io } from 'socket.io-client';
import config from '../config';
import { getAccessToken } from './authStorage';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.connectionState = 'disconnected';
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.lastError = null;
  }

  connect() {
    if (this.socket) {
      const managerState = this.socket.io?._readyState;
      if (this.socket.connected || managerState === 'opening' || managerState === 'open') {
        return;
      }

      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        return;
      }

      this.socket.removeAllListeners();
      this.socket.close();
      this.socket = null;
    }

    const token = getAccessToken();
    if (!token) {
      console.warn('WebSocket: No auth token, skipping connection');
      return;
    }

    // Use wsUrl if specified, otherwise use origin (for same-server Socket.io)
    const wsUrl = config.wsUrl || window.location.origin;
    console.log('WebSocket: Connecting to', wsUrl);
    this.connectionState = 'connecting';
    
    this.socket = io(wsUrl, {
      auth: { token },
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      reconnectionAttempts: this.maxReconnectAttempts,
    });

    this.socket.on('connect', () => {
      console.log('WebSocket: Connected', this.socket.id);
      this.connectionState = 'connected';
      this.reconnectAttempts = 0;
      this.lastError = null;
      this.emit('connectionChange', { state: 'connected' });
    });

    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket: Disconnected', reason);
      this.connectionState = 'disconnected';
      this.emit('connectionChange', { state: 'disconnected', reason });
    });

    this.socket.on('connect_error', (error) => {
      this.lastError = error?.message || 'Unknown connection error';
      console.warn('WebSocket: Connection error', this.lastError);
      this.connectionState = 'error';
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.warn('WebSocket: Reconnect limit reached, pausing further attempts until a manual reconnect');
        this.emit('connectionChange', {
          state: 'failed',
          attempts: this.reconnectAttempts,
          reason: this.lastError,
        });

        if (this.socket) {
          this.socket.removeAllListeners();
          this.socket.close();
          this.socket = null;
        }

        return;
      }

      this.emit('connectionChange', {
        state: 'reconnecting',
        attempts: this.reconnectAttempts,
        reason: this.lastError,
      });
    });

    // M004-F0077: Booking events
    this.socket.on('booking:created', (data) => {
      console.log('WebSocket: booking:created', data);
      this.emit('booking:created', data);
    });
    this.socket.on('booking:approved', (data) => {
      console.log('WebSocket: booking:approved', data);
      this.emit('booking:approved', data);
    });
    this.socket.on('booking:rejected', (data) => {
      console.log('WebSocket: booking:rejected', data);
      this.emit('booking:rejected', data);
    });
    this.socket.on('booking:updated', (data) => {
      console.log('WebSocket: booking:updated', data);
      this.emit('booking:updated', data);
    });
    this.socket.on('booking:deleted', (data) => {
      console.log('WebSocket: booking:deleted', data);
      this.emit('booking:deleted', data);
    });

    // M004-F0079: Notification events
    this.socket.on('notification:new', (data) => {
      console.log('WebSocket: notification:new', data);
      this.emit('notification:new', data);
    });

    // M017 network monitor events
    this.socket.on('network:device_discovered', (data) => {
      console.log('WebSocket: network:device_discovered', data);
      this.emit('network:device_discovered', data);
    });
    this.socket.on('network:device_offline', (data) => {
      console.log('WebSocket: network:device_offline', data);
      this.emit('network:device_offline', data);
    });
    this.socket.on('network:anomaly', (data) => {
      console.log('WebSocket: network:anomaly', data);
      this.emit('network:anomaly', data);
    });
    this.socket.on('network:health_alert', (data) => {
      console.log('WebSocket: network:health_alert', data);
      this.emit('network:health_alert', data);
    });
    this.socket.on('network:scan_complete', (data) => {
      console.log('WebSocket: network:scan_complete', data);
      this.emit('network:scan_complete', data);
    });
    this.socket.on('packet-stats:current', (data) => {
      console.log('WebSocket: packet-stats:current', data);
      this.emit('packet-stats:current', data);
    });
    this.socket.on('packet-stats:10s', (data) => {
      console.log('WebSocket: packet-stats:10s', data);
      this.emit('packet-stats:10s', data);
    });
    this.socket.on('packet-stats:1m', (data) => {
      console.log('WebSocket: packet-stats:1m', data);
      this.emit('packet-stats:1m', data);
    });
    this.socket.on('security:snapshot', (data) => {
      console.log('WebSocket: security:snapshot', data);
      this.emit('security:snapshot', data);
    });
    this.socket.on('security:feed_online', (data) => {
      console.log('WebSocket: security:feed_online', data);
      this.emit('security:feed_online', data);
    });
    this.socket.on('security:feed_offline', (data) => {
      console.log('WebSocket: security:feed_offline', data);
      this.emit('security:feed_offline', data);
    });
    this.socket.on('security:feed_updated', (data) => {
      console.log('WebSocket: security:feed_updated', data);
      this.emit('security:feed_updated', data);
    });
    this.socket.on('security:device_status', (data) => {
      console.log('WebSocket: security:device_status', data);
      this.emit('security:device_status', data);
    });

    // Legacy event mappings for backward compatibility
    this.socket.on('occupancy:update', (data) => this.emit('occupancy', data));
    this.socket.on('alert:new', (data) => this.emit('alert', data));
    this.socket.on('climate:update', (data) => this.emit('climate', data));
    this.socket.on('system:health', (data) => this.emit('systemHealth', data));
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.connectionState = 'disconnected';
    this.reconnectAttempts = 0;
  }

  subscribe(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
    
    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  emit(event, data) {
    this.listeners.get(event)?.forEach((callback) => callback(data));
  }

  // Subscribe to organisation-specific room
  subscribeToOrg(orgId) {
    if (this.socket?.connected) {
      this.socket.emit('subscribe:org', orgId);
    }
  }

  // Subscribe to room-specific updates
  subscribeToRoom(roomId) {
    if (this.socket?.connected) {
      this.socket.emit('subscribe:room', roomId);
    }
  }

  getConnectionState() {
    return this.connectionState;
  }

  getLastError() {
    return this.lastError;
  }

  isConnected() {
    return this.socket?.connected || false;
  }

  /**
   * BUG-012 Fix: Update authentication token for WebSocket connection
   * Call this when the user's JWT token is refreshed
   * @param {string} newToken - The new JWT token
   */
  updateToken(newToken) {
    if (!newToken) {
      console.warn('WebSocket: Cannot update token - no token provided');
      return;
    }

    if (this.socket) {
      // Update the auth token for future reconnections
      this.socket.auth = { token: newToken };
      
      // If connected, disconnect and reconnect with new token
      if (this.socket.connected) {
        console.log('WebSocket: Reconnecting with new token');
        this.socket.disconnect();
        this.socket.connect();
      }
    } else {
      // No socket exists, just connect with the new token
      console.log('WebSocket: Connecting with new token');
      this.connect();
    }
  }

  /**
   * Reconnect with the current full-session token.
   * Useful when token has been refreshed elsewhere
   */
  reconnectWithFreshToken() {
    const token = getAccessToken();
    if (token) {
      this.updateToken(token);
    } else {
      console.warn('WebSocket: No token available for reconnection');
      this.disconnect();
    }
  }
}

// Singleton instance
const wsService = new WebSocketService();
export default wsService;
